import { useState } from 'react';

const serviceAreas = [
  { name: '진주시', primary: true, desc: '본사 위치 · 당일 출동 가능' },
  { name: '사천시', primary: false, desc: '정기 방문 서비스' },
  { name: '고성군', primary: false, desc: '정기 방문 서비스' },
  { name: '하동군', primary: false, desc: '정기 방문 서비스' },
  { name: '산청군', primary: false, desc: '정기 방문 서비스' },
];

export default function ServiceArea() {
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);

  return (
    <section className="relative py-20 overflow-hidden" id="service-area" style={{background: 'linear-gradient(145deg, #0d1f3c 0%, #111e3a 55%, #152a52 100%)'}}>
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(122deg, transparent 43%, #c9a227 44%, #c9a227 44.4%, transparent 45%), linear-gradient(76deg, transparent 61%, #c9a227 62%, #c9a227 62.3%, transparent 63%)', backgroundSize: '360px 360px'}}></div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-[#c9a227]/15 text-[#c9a227] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4 border border-[#c9a227]/30">
            Service Area
          </span>
          <h2 className="text-3xl font-bold text-white mb-3">서비스 지역 안내</h2>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            진주를 중심으로 경남 서부권 전역에 정원 관리 및 벌초 서비스를 제공합니다.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-stretch">
          {/* Map Image */}
          <div className="flex-1 rounded-2xl overflow-hidden shadow-lg border border-[#c9a227]/20 relative" style={{ height: '480px' }}>
            <img
              src="https://public.readdy.ai/ai/img_res/dbd1c14fb16b5665f48da6b2850829f2.jpg"
              alt="진주시 서비스 지역 지도"
              className="w-full h-full object-cover object-center"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/70 via-transparent to-transparent" />
            {/* Pin */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-[#c9a227] flex items-center justify-center shadow-lg border-2 border-white">
                <i className="ri-map-pin-fill text-white text-lg"></i>
              </div>
              <span className="mt-1 text-xs font-bold text-white bg-[#0d1f3c]/80 px-2 py-0.5 rounded-full border border-[#c9a227]/40">진주시 본사</span>
            </div>
            {/* Open Map Button */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <a
                href="https://maps.google.com/?q=진주시+경상남도"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-lg whitespace-nowrap hover:opacity-90 transition-opacity text-[#0d1f3c] shadow-md"
                style={{background: 'linear-gradient(135deg, #e8c84a, #b8860b)'}}
              >
                <i className="ri-external-link-line"></i>
                Google 지도에서 보기
              </a>
            </div>
          </div>

          {/* Area List */}
          <div className="lg:w-80 flex flex-col gap-3">
            {/* Legend */}
            <div className="flex items-center gap-4 mb-2 px-1">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#c9a227] inline-block"></span>
                <span className="text-xs text-white/55">당일 출동</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#c9a227]/40 inline-block"></span>
                <span className="text-xs text-white/55">정기 방문</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-white/20 inline-block"></span>
                <span className="text-xs text-white/55">협의 가능</span>
              </div>
            </div>

            {serviceAreas.map((area) => {
              const dotColor = area.primary ? 'bg-[#c9a227]' : area.desc.includes('협의') ? 'bg-white/20' : 'bg-[#c9a227]/40';
              const isHovered = hoveredArea === area.name;

              return (
                <div
                  key={area.name}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-all duration-200 cursor-default ${isHovered ? 'border-[#c9a227]/40' : 'border-[#c9a227]/15'}`}
                  style={{background: isHovered ? 'rgba(201,162,39,0.12)' : 'rgba(30,58,110,0.5)'}}
                  onMouseEnter={() => setHoveredArea(area.name)}
                  onMouseLeave={() => setHoveredArea(null)}
                >
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${dotColor}`}></span>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${area.primary ? 'text-[#c9a227]' : 'text-white/85'}`}>
                      {area.name}
                      {area.primary && (
                        <span className="ml-2 text-xs bg-[#c9a227]/15 text-[#c9a227] px-2 py-0.5 rounded-full font-medium border border-[#c9a227]/30">
                          본사
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-white/45 mt-0.5">{area.desc}</p>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center text-white/30">
                    <i className="ri-map-pin-line text-sm"></i>
                  </div>
                </div>
              );
            })}

            {/* CTA */}
            <div className="mt-4 rounded-xl px-5 py-4 border border-[#c9a227]/25" style={{background: 'linear-gradient(135deg, rgba(201,162,39,0.15), rgba(184,134,11,0.1))'}}>
              <p className="text-sm font-semibold mb-1 text-[#c9a227]">지역 문의</p>
              <p className="text-xs text-white/55 mb-3">해당 지역 외에도 상담 후 방문 가능합니다.</p>
              <a
                href="tel:010-9879-8375"
                className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-lg whitespace-nowrap hover:opacity-90 transition-opacity text-[#0d1f3c]"
                style={{background: 'linear-gradient(135deg, #e8c84a, #b8860b)'}}
              >
                <i className="ri-phone-line"></i>
                전화 상담하기
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
